"use client";

import React, { useState, useMemo } from "react";
import { Maximize2, Clock, CircleCheck, MapPin } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
const projects = [{
  id: "proj-1",
  title: "Modern Estate Landscaping",
  category: "Residential",
  service: "Professional Landscaping",
  location: "Springfield North",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/project_landscaping_1.png",
  summary: "Complete overhaul of front and backyard including new sod, drought-resistant plants, and a custom stone pathway to enhance curb appeal.",
  duration: "2 Weeks",
  servicesPerformed: "Landscape Design, Sod Installation, Hardscaping, Irrigation Setup"
}, {
  id: "proj-2",
  title: "Corporate Office Exterior",
  category: "Commercial",
  service: "Exterior Painting",
  location: "Downtown Business District",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/project_exterior_painting_1.png",
  summary: "Full exterior repaint of a 3-story office building using weather-resistant, premium commercial-grade paint to protect and beautify the facade.",
  duration: "3 Weeks",
  servicesPerformed: "Power Washing, Surface Preparation, Exterior Painting, Weather Sealing"
}, {
  id: "proj-3",
  title: "Suburban Home Deep Clean",
  category: "Residential",
  service: "High-Pressure Power Washing",
  location: "Westside Suburbs",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/project_power_washing_1.png",
  summary: "Comprehensive deep cleaning of driveway, walkways, and home siding to remove years of built-up grime, mildew, and environmental stains.",
  duration: "1 Day",
  servicesPerformed: "Driveway Power Washing, Siding Soft Wash, Mildew Treatment"
}, {
  id: "proj-4",
  title: "Retail Space Post-Renovation",
  category: "Commercial",
  service: "Janitorial and Cleaning Services",
  location: "Springfield Mall",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/project_janitorial_1.png",
  summary: "Post-construction deep clean for a newly renovated retail space, ensuring every surface was spotless and ready for the grand opening event.",
  duration: "3 Days",
  servicesPerformed: "Debris Removal, Deep Dusting, Window Washing, Floor Polishing"
}, {
  id: "proj-5",
  title: "Luxury Condo Interior",
  category: "Residential",
  service: "Interior Painting",
  location: "Riverside Towers",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/project_interior_painting_1.png",
  summary: "Meticulous interior painting of a large open-concept living and dining area, including detailed trim work and ceiling touch-ups.",
  duration: "4 Days",
  servicesPerformed: "Color Consultation, Drywall Repair, Trim Painting, Wall Painting"
}, {
  id: "proj-6",
  title: "Apartment Complex Grounds",
  category: "Commercial",
  service: "Professional Landscaping",
  location: "East End Apartments",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577498/img/project_landscaping_2.png",
  summary: "Ongoing maintenance and seasonal planting for a large residential apartment complex to maintain a welcoming environment for tenants.",
  duration: "Ongoing Contract",
  servicesPerformed: "Lawn Care, Pruning, Seasonal Planting, Mulching"
}];
const filters = ["All", "Residential", "Commercial", "Landscaping", "Painting", "Power Washing", "Cleaning"];
export default function ProjectGallery() {
  const [activeFilter, setActiveFilter] = useState("All");
  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      if (activeFilter === "All") return true;
      if (activeFilter === "Residential" || activeFilter === "Commercial") return p.category === activeFilter;
      if (activeFilter === "Landscaping") return p.service === "Professional Landscaping";
      if (activeFilter === "Painting") return p.service === "Interior Painting" || p.service === "Exterior Painting";
      if (activeFilter === "Power Washing") return p.service === "High-Pressure Power Washing";
      if (activeFilter === "Cleaning") return p.service === "Janitorial and Cleaning Services";
      return true;
    });
  }, [activeFilter]);
  return <section id="project-gallery" className="bg-background py-24 sm:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col items-center text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Our Project Gallery
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Explore a visual portfolio of Elyon Maintenance Inc.'s high-tier craftsmanship across residential and commercial properties.
          </p>
        </div>

        <div className="flex justify-center mb-10 overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 hide-scrollbar">
          <ToggleGroup type="single" value={activeFilter} onValueChange={value => {
          if (value) setActiveFilter(value);
        }} className="justify-start sm:justify-center">
            {filters.map((filter, i) => <ToggleGroupItem key={filter} value={filter} data-index={i} className="whitespace-nowrap px-4 py-2 text-sm font-medium transition-colors data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                {filter}
              </ToggleGroupItem>)}
          </ToggleGroup>
        </div>

        {}
        <div key={activeFilter} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filteredProjects.map((project, i) => <div key={project.id} data-index={i} className="animate-in fade-in zoom-in-95 duration-500 fill-mode-both" style={{
          animationDelay: `${i * 100}ms`
        }}>
              <Dialog>
                <DialogTrigger asChild>
                  <div className="group relative cursor-pointer overflow-hidden rounded-2xl bg-muted aspect-[4/3] shadow-sm transition-shadow hover:shadow-md">
                    <img src={project.image} alt={project.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
                    
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <div className="bg-primary/90 text-primary-foreground p-3 rounded-full transform translate-y-4 transition-transform duration-300 group-hover:translate-y-0">
                        <Maximize2 className="size-6" />
                      </div>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 p-6 text-left">
                      <span className="inline-block px-2.5 py-1 rounded-md bg-primary/20 text-primary-foreground backdrop-blur-md text-xs font-semibold mb-2 border border-primary/30">
                        {project.category}
                      </span>
                      <h3 className="text-xl font-bold text-white mb-1">
                        {project.title}
                      </h3>
                      <p className="text-white/80 text-sm line-clamp-1">
                        {project.service}
                      </p>
                    </div>
                  </div>
                </DialogTrigger>
                
                <DialogContent className="sm:max-w-4xl p-0 overflow-hidden gap-0 bg-background border-border shadow-2xl">
                  <div className="grid md:grid-cols-5 h-full max-h-[90vh] overflow-y-auto md:overflow-hidden">
                    <div className="md:col-span-3 relative h-64 md:h-auto bg-muted">
                      <img src={project.image} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    <div className="md:col-span-2 p-6 md:p-8 flex flex-col bg-card">
                      <DialogHeader className="text-left mb-6">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="px-2.5 py-1 rounded-md bg-primary/10 text-primary text-xs font-semibold">
                            {project.category}
                          </span>
                          <span className="text-muted-foreground text-sm">
                            {project.service}
                          </span>
                        </div>
                        <DialogTitle className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                          {project.title}
                        </DialogTitle>
                        <DialogDescription className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <MapPin className="size-4" />
                          {project.location}
                        </DialogDescription>
                      </DialogHeader>

                      <div className="space-y-6 flex-1">
                        <div>
                          <h4 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-2">
                            Project Summary
                          </h4>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {project.summary}
                          </p>
                        </div>

                        <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 border border-border/50">
                          <div className="bg-background p-2 rounded-md shadow-xs">
                            <Clock className="size-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground font-medium">Duration</p>
                            <p className="text-sm font-semibold text-foreground">{project.duration}</p>
                          </div>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-3">
                            Services Performed
                          </h4>
                          <ul className="space-y-2.5">
                            {project.servicesPerformed.split(", ").map((service, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                                <CircleCheck className="size-4 text-primary shrink-0 mt-0.5" />
                                <span>{service}</span>
                              </li>)}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>

        {filteredProjects.length === 0 && <div className="text-center py-20">
            <p className="text-muted-foreground text-lg">No projects found for this category.</p>
          </div>}
      </div>
    </section>;
}